import { getOfflineStorage, OfflineStorage } from './storage';
import { getNetworkService, NetworkService } from './network';
import { getSyncService, SyncService } from './sync';
import {
    createCredentialCacheRecord,
    createSessionCacheRecord,
    credentialKey,
    getCachedSessionData,
    getCachedSessionRecord,
    isCredentialUsable,
    normalizeSessionUser,
    resolveCachedUserId,
    toBool
} from './auth-session';
import $ from 'jquery';

interface OfflineHandler {
    read?: (params: any, storage: OfflineStorage) => Promise<any>;
    write?: (params: any, storage: OfflineStorage, sync: SyncService, baseuri: string, namespace: string, funcName: string) => Promise<any>;
    cache?: (result: any, params: any, storage: OfflineStorage) => Promise<void>;
}

const OFFLINE_LOGIN_UNAVAILABLE_MESSAGE = '오프라인 로그인을 위해서는 이 기기에서 30일 이내에 동일 계정으로 온라인 로그인을 한 번 완료해야 합니다.';
const DEFAULT_REQUEST_TIMEOUT_MS = 10000;

async function credentialDigest(userId: string, passwordHash: string): Promise<string> {
    const value = `${userId}:${passwordHash}`;
    const cryptoApi = (window as any).crypto;
    if (!cryptoApi || !cryptoApi.subtle || typeof TextEncoder === 'undefined') {
        return value;
    }

    const bytes = new TextEncoder().encode(value);
    const digest = await cryptoApi.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}

function parseJson(value: any, fallback: any) {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value !== 'string') return value;
    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
}

type ChecklistResponseStatus = 'draft' | 'submit_pending' | 'submitted' | 'sync_failed';

function normalizeChecklistStatus(status: any): ChecklistResponseStatus {
    if (status === 'submitted') return 'submitted';
    if (status === 'submit_pending') return 'submit_pending';
    if (status === 'sync_failed') return 'sync_failed';
    return 'draft';
}

function isLocalChecklistStatus(status: any): boolean {
    const normalized = normalizeChecklistStatus(status);
    return normalized === 'submit_pending' || normalized === 'sync_failed';
}

function countChecklistPhotos(answers: any): number {
    if (!answers || typeof answers !== 'object') return 0;
    let count = 0;
    for (const answer of Object.values(answers)) {
        const photos = (answer && typeof answer === 'object') ? (answer as any).photos : null;
        if (Array.isArray(photos)) count += photos.length;
    }
    return count;
}

function getChecklistPhotoSyncStatus(status: ChecklistResponseStatus, answers: any): string {
    const photoCount = countChecklistPhotos(answers);
    if (photoCount === 0) return 'none';
    if (status === 'submitted') return 'synced';
    if (status === 'submit_pending') return 'pending';
    if (status === 'sync_failed') return 'failed';
    return 'local';
}

function checklistResponseKey(responseId: string) {
    return `checklist_response:${responseId}`;
}

function checklistResultKey(responseId: string) {
    return `checklist_result:${responseId}`;
}

function checklistMappingKey(mappingId: string) {
    return `checklist_mapping_response:${mappingId}`;
}

function inspectionViewerSyncKey(drawingId: string) {
    return `inspection_viewer_sync:${drawingId}`;
}

function inspectionViewerOverviewKey(facilityId: string) {
    return `inspection_viewer_overview:${facilityId}`;
}

function selectedProjectsKey(userId: string) {
    return `selected_projects:${userId || 'unknown'}`;
}

function checklistTimestamp() {
    return new Date().toISOString().slice(0, 19).replace('T', ' ');
}

function currentUserIdFromAuth(user: any): string {
    return resolveCachedUserId(user);
}

async function getCurrentUserId(storage: OfflineStorage): Promise<string> {
    const user = await storage.get('auth', 'current_user');
    return currentUserIdFromAuth(user);
}

function userIdFromResult(result: any): string {
    const data = result && result.data ? result.data : {};
    if (!data || typeof data !== 'object' || Array.isArray(data)) return '';
    return String(data.user_id || currentUserIdFromAuth(data.user) || '').trim();
}

function projectIdOf(project: any): string {
    return String((project && (project.id || project.project_id)) || '').trim();
}

function parseProjectDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }

    const text = String(value).slice(0, 10);
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
    if (!match) return null;

    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(date.getTime()) ? null : date;
}

function normalizeProjectPeriod(project: any): any {
    const next = { ...(project || {}) };
    const startDate = parseProjectDate(next.start_date);
    const endDate = parseProjectDate(next.end_date);

    if (!startDate || !endDate) {
        next.period_status = next.period_status || '-';
        next.status_color = next.status_color || 'none';
        return next;
    }

    const nowValue = new Date();
    const today = new Date(nowValue.getFullYear(), nowValue.getMonth(), nowValue.getDate()).getTime();
    const start = startDate.getTime();
    const end = endDate.getTime();

    if (today < start) {
        next.period_status = '예정';
        next.status_color = 'planned';
    } else if (today >= start && today <= end) {
        next.period_status = '진행중';
        next.status_color = 'ongoing';
    } else {
        next.period_status = '완료';
        next.status_color = 'completed';
    }

    return next;
}

function extractSelectedProjects(result: any): any[] | null {
    if (!result || result.code !== 200) return null;
    const data = result.data;
    if (Array.isArray(data)) return data;
    if (!data || typeof data !== 'object') return null;

    const keys = ['projects', 'selected_projects', 'selectedProjects'];
    for (const key of keys) {
        if (Array.isArray(data[key])) return data[key];
    }
    return null;
}

async function getSelectionRowsForUser(storage: OfflineStorage, userId: string): Promise<any[]> {
    if (!userId) return [];
    try {
        return await storage.getAllByIndex('user_selections', 'user_id', userId);
    } catch {
        const selections = await storage.getAll('user_selections');
        return selections.filter((selection: any) => String(selection.user_id || '') === userId);
    }
}

async function getCachedProjectById(storage: OfflineStorage, projectId: any): Promise<any> {
    const direct = await storage.get('projects', projectId);
    if (direct) return direct;
    const normalized = String(projectId || '').trim();
    if (!normalized || normalized === projectId) return null;
    return storage.get('projects', normalized);
}

function normalizeCacheId(value: any): string {
    return String(value || '').trim();
}

async function getCachedRecordById(storage: OfflineStorage, storeName: string, id: any): Promise<any> {
    const normalized = normalizeCacheId(id);
    if (!normalized) return null;
    const direct = await storage.get(storeName, id);
    if (direct) return direct;
    if (normalized !== id) return storage.get(storeName, normalized);
    return null;
}

async function getCachedRowsByIndex(storage: OfflineStorage, storeName: string, indexName: string, value: any): Promise<any[]> {
    const normalized = normalizeCacheId(value);
    if (!normalized) return [];
    try {
        return await storage.getAllByIndex(storeName, indexName, normalized);
    } catch {
        const rows = await storage.getAll(storeName);
        return rows.filter((row: any) => normalizeCacheId(row && row[indexName]) === normalized);
    }
}

function facilityDrawingKey(facilityId: any, drawingId: any): string {
    return `${normalizeCacheId(facilityId)}_${normalizeCacheId(drawingId)}`;
}

async function cacheFacilityDrawingMapping(storage: OfflineStorage, facilityId: any, drawingId: any): Promise<void> {
    const normalizedFacilityId = normalizeCacheId(facilityId);
    const normalizedDrawingId = normalizeCacheId(drawingId);
    if (!normalizedFacilityId || !normalizedDrawingId) return;
    await storage.put('facility_drawings', {
        id: facilityDrawingKey(normalizedFacilityId, normalizedDrawingId),
        facility_id: normalizedFacilityId,
        drawing_id: normalizedDrawingId,
        cached_at: Date.now()
    });
}

function drawingIdOf(value: any): string {
    if (!value) return '';
    if (typeof value === 'string' || typeof value === 'number') return normalizeCacheId(value);
    return normalizeCacheId(value.id || value.drawing_id);
}

function drawingListFromFacility(facility: any): any[] {
    if (Array.isArray(facility?.drawings)) return facility.drawings;
    const parsed = parseJson(facility?.drawing_ids, []);
    if (Array.isArray(parsed)) return parsed;
    return [];
}

function hasCachedFileData(file: any): boolean {
    return !!(file && (file.data || file.blob));
}

async function mergeAndCacheFacility(storage: OfflineStorage, facility: any): Promise<any> {
    if (!facility || !facility.id) return null;
    const existing = await getCachedRecordById(storage, 'facilities', facility.id);
    const merged = {
        ...(existing || {}),
        ...facility
    };
    if (!Array.isArray(merged.overviewPhotos) && Array.isArray(existing?.overviewPhotos)) {
        merged.overviewPhotos = existing.overviewPhotos;
    }
    await storage.put('facilities', merged);
    return merged;
}

async function cacheProjectDetailData(storage: OfflineStorage, data: any): Promise<void> {
    if (!data || typeof data !== 'object') return;

    if (data.project && data.project.id) await storage.put('projects', data.project);
    if (Array.isArray(data.drawings) && data.drawings.length > 0) await storage.putMany('drawings', data.drawings);

    if (Array.isArray(data.facility_drawings)) {
        for (const row of data.facility_drawings) {
            const facilityId = row?.facility_id;
            const drawingId = row?.drawing_id;
            if (facilityId && drawingId) await cacheFacilityDrawingMapping(storage, facilityId, drawingId);
        }
    }

    if (Array.isArray(data.facilities)) {
        for (const facility of data.facilities) {
            const cachedFacility = await mergeAndCacheFacility(storage, facility);
            for (const drawing of drawingListFromFacility(facility)) {
                const drawingId = drawingIdOf(drawing);
                if (!drawingId) continue;
                if (drawing && typeof drawing === 'object') await storage.put('drawings', drawing);
                await cacheFacilityDrawingMapping(storage, cachedFacility?.id || facility.id, drawingId);
            }
        }
    }
}

async function getCachedDrawingsForFacility(storage: OfflineStorage, facility: any): Promise<any[]> {
    const facilityId = normalizeCacheId(facility?.id);
    const drawings: any[] = [];
    const seen = new Set<string>();

    const pushDrawing = async (drawingRef: any) => {
        const drawingId = drawingIdOf(drawingRef);
        if (!drawingId || seen.has(drawingId)) return;
        seen.add(drawingId);
        let drawing = drawingRef && typeof drawingRef === 'object' ? drawingRef : null;
        if (!drawing || !drawing.id) drawing = await getCachedRecordById(storage, 'drawings', drawingId);
        if (drawing) drawings.push(drawing);
    };

    if (facilityId) {
        const mappings = await getCachedRowsByIndex(storage, 'facility_drawings', 'facility_id', facilityId);
        for (const mapping of mappings) await pushDrawing(mapping.drawing_id);
    }

    if (drawings.length === 0) {
        for (const drawing of drawingListFromFacility(facility)) await pushDrawing(drawing);
    }

    return drawings;
}

async function readCachedProjectDetail(storage: OfflineStorage, projectId: any): Promise<any> {
    const normalizedProjectId = normalizeCacheId(projectId);
    const project = await getCachedProjectById(storage, normalizedProjectId);
    if (!project) return { code: 404, data: null, message: '캐시된 프로젝트 데이터가 없습니다.' };

    const drawings = await getCachedRowsByIndex(storage, 'drawings', 'project_id', normalizedProjectId);
    const facilities = await getCachedRowsByIndex(storage, 'facilities', 'project_id', normalizedProjectId);
    for (const facility of facilities) {
        const linkedDrawings = await getCachedDrawingsForFacility(storage, facility);
        facility.drawings = linkedDrawings;
        facility.drawing_count = linkedDrawings.length || Number(facility.drawing_count || 0);
    }

    return {
        code: 200,
        data: {
            project,
            drawings,
            facilities,
            offline: true
        }
    };
}

async function readCachedFacilityDetail(storage: OfflineStorage, facilityId: any): Promise<any> {
    const normalizedFacilityId = normalizeCacheId(facilityId);
    const facility = await getCachedRecordById(storage, 'facilities', normalizedFacilityId);
    if (!facility) return { code: 404, data: null, message: '캐시된 시설물 데이터가 없습니다.' };

    const drawings = await getCachedDrawingsForFacility(storage, facility);
    const overviewPhotos = await storage.getMeta(inspectionViewerOverviewKey(normalizedFacilityId));
    return {
        code: 200,
        data: {
            facility,
            drawings,
            overview_photos: Array.isArray(overviewPhotos) ? overviewPhotos : (facility.overviewPhotos || []),
            offline: true
        }
    };
}

async function writeSelectedProjectSnapshot(
    storage: OfflineStorage,
    userId: string,
    projectIds: string[],
    projects: any[]
): Promise<void> {
    await storage.setMeta(selectedProjectsKey(userId), {
        user_id: userId,
        project_ids: projectIds,
        projects,
        cached_at: Date.now()
    });
}

async function cacheSelectedProjectList(
    storage: OfflineStorage,
    projects: any[],
    explicitUserId: string = ''
): Promise<void> {
    const userId = explicitUserId || await getCurrentUserId(storage);
    const normalizedProjects = projects
        .filter((project: any) => project && projectIdOf(project))
        .map((project: any) => normalizeProjectPeriod(project));

    if (normalizedProjects.length > 0) {
        await storage.putMany('projects', normalizedProjects);
    }

    if (!userId) return;

    const projectIds: string[] = [];
    const seen = new Set<string>();
    for (const project of normalizedProjects) {
        const projectId = projectIdOf(project);
        if (!projectId || seen.has(projectId)) continue;
        seen.add(projectId);
        projectIds.push(projectId);
        await storage.put('user_selections', {
            id: `${userId}_${projectId}`,
            user_id: userId,
            project_id: projectId,
            source: 'online_selected_projects',
            cached_at: Date.now()
        });
    }

    const existing = await getSelectionRowsForUser(storage, userId);
    for (const selection of existing) {
        const projectId = String(selection.project_id || '').trim();
        if (projectId && !seen.has(projectId)) {
            await storage.delete('user_selections', selection.id);
        }
    }

    await writeSelectedProjectSnapshot(storage, userId, projectIds, normalizedProjects);
}

async function cacheSelectedProjectsFromResult(result: any, storage: OfflineStorage): Promise<void> {
    const projects = extractSelectedProjects(result);
    if (projects === null) return;
    await cacheSelectedProjectList(storage, projects, userIdFromResult(result));
}

async function readCachedSelectedProjects(storage: OfflineStorage): Promise<any[]> {
    const userId = await getCurrentUserId(storage);
    if (!userId) return [];

    const snapshot = await storage.getMeta(selectedProjectsKey(userId));
    const snapshotProjects = Array.isArray(snapshot?.projects) ? snapshot.projects : [];
    const snapshotProjectById: Record<string, any> = {};
    for (const project of snapshotProjects) {
        const projectId = projectIdOf(project);
        if (projectId) snapshotProjectById[projectId] = project;
    }

    let projectIds = (await getSelectionRowsForUser(storage, userId))
        .map((selection: any) => String(selection.project_id || '').trim())
        .filter((projectId: string) => !!projectId);

    if (projectIds.length === 0 && Array.isArray(snapshot?.project_ids)) {
        projectIds = snapshot.project_ids
            .map((projectId: any) => String(projectId || '').trim())
            .filter((projectId: string) => !!projectId);
    }

    if (projectIds.length === 0 && snapshotProjects.length > 0) {
        return snapshotProjects.map((project: any) => normalizeProjectPeriod(project));
    }

    const projects = [];
    const seen = new Set<string>();
    for (const projectId of projectIds) {
        if (seen.has(projectId)) continue;
        seen.add(projectId);
        const project = await getCachedProjectById(storage, projectId) || snapshotProjectById[projectId];
        if (project) projects.push(normalizeProjectPeriod(project));
    }

    return projects;
}

async function addCachedProjectSelection(storage: OfflineStorage, userId: string, projectId: string): Promise<void> {
    if (!userId || !projectId) return;
    await storage.put('user_selections', {
        id: `${userId}_${projectId}`,
        user_id: userId,
        project_id: projectId,
        source: 'local_selection',
        cached_at: Date.now()
    });

    const projects = await readCachedSelectedProjects(storage);
    const project = await getCachedProjectById(storage, projectId);
    if (project && !projects.some((entry: any) => projectIdOf(entry) === projectId)) {
        projects.push(normalizeProjectPeriod(project));
    }
    await writeSelectedProjectSnapshot(storage, userId, projects.map((entry: any) => projectIdOf(entry)), projects);
}

async function removeCachedProjectSelection(storage: OfflineStorage, userId: string, projectId: string): Promise<void> {
    if (!userId || !projectId) return;
    await storage.delete('user_selections', `${userId}_${projectId}`);
    const snapshot = await storage.getMeta(selectedProjectsKey(userId));
    const projectIds = Array.isArray(snapshot?.project_ids)
        ? snapshot.project_ids.map((id: any) => String(id || '').trim()).filter((id: string) => id && id !== projectId)
        : [];
    const projects = Array.isArray(snapshot?.projects)
        ? snapshot.projects.filter((project: any) => projectIdOf(project) !== projectId).map((project: any) => normalizeProjectPeriod(project))
        : [];
    await writeSelectedProjectSnapshot(storage, userId, projectIds, projects);
}

function applyChecklistMeta(response: any, meta: any, status?: string) {
    const next = { ...(response || {}) };
    const allowed = [
        'total_score', 'max_score', 'safety_grade', 'inspector_name',
        'inspection_date', 'overall_opinion', 'emergency_inspection',
        'safety_action'
    ];

    for (const key of allowed) {
        if (meta && meta[key] !== undefined) next[key] = meta[key];
    }

    if (status) next.status = status;
    if (!next.status) next.status = 'draft';
    next.updated = checklistTimestamp();
    return next;
}

async function cacheChecklistAggregate(storage: OfflineStorage, data: any) {
    if (!data || !data.response || !data.response.id) return;
    const responseId = data.response.id;
    const current = await storage.getMeta(checklistResponseKey(responseId));
    const incomingStatus = normalizeChecklistStatus(data.response.status);
    const currentStatus = normalizeChecklistStatus(current && current.response ? current.response.status : '');
    const currentSyncSource = current && current.response ? current.response.sync_source : '';
    const currentSyncStatus = current && current.response ? current.response.sync_status : '';
    const hasUnsyncedLocalDraft = currentStatus === 'draft'
        && currentSyncSource === 'local'
        && currentSyncStatus !== 'synced';
    let next = {
        ...data,
        response: {
            ...data.response,
            status: incomingStatus,
            sync_source: 'server',
            sync_status: 'synced',
            photo_sync_status: data.response.photo_sync_status || getChecklistPhotoSyncStatus(incomingStatus, data.answers)
        }
    };

    if (current && current.response && (isLocalChecklistStatus(currentStatus) || hasUnsyncedLocalDraft) && incomingStatus !== 'submitted') {
        next = {
            ...data,
            response: {
                ...data.response,
                ...current.response,
                status: currentStatus,
                sync_source: 'local',
                sync_status: currentStatus === 'sync_failed' ? 'failed' : (current.response.sync_status || 'pending'),
                photo_sync_status: current.response.photo_sync_status || getChecklistPhotoSyncStatus(currentStatus, current.answers || data.answers)
            },
            answers: current.answers || data.answers
        };
    }

    await storage.setMeta(checklistResponseKey(responseId), next);
    await storage.setMeta(checklistResultKey(responseId), next);
    if (data.response.mapping_id) {
        await storage.setMeta(checklistMappingKey(data.response.mapping_id), {
            response_id: responseId,
            status: next.response.status || 'draft',
            cached_at: Date.now()
        });
    }
    if (data.facility && data.facility.id) await storage.put('facilities', data.facility);
    if (data.project && data.project.id) await storage.put('projects', data.project);
}

async function updateCachedChecklistMapping(storage: OfflineStorage, mappingId: string, responseId: string, status: string) {
    if (!mappingId) return;

    await storage.setMeta(checklistMappingKey(mappingId), {
        response_id: responseId,
        status,
        cached_at: Date.now()
    });

    const facilities = await storage.getAll('facilities');
    for (const facility of facilities) {
        const checklists = Array.isArray(facility.checklists) ? facility.checklists : [];
        let changed = false;
        for (const mapping of checklists) {
            if (mapping && mapping.mapping_id === mappingId) {
                mapping.response_id = responseId;
                mapping.response_status = status;
                changed = true;
            }
        }
        if (changed) await storage.put('facilities', facility);
    }
}

async function findCachedChecklistMapping(storage: OfflineStorage, mappingId: string) {
    const cached = await storage.getMeta(checklistMappingKey(mappingId));
    if (cached && cached.response_id) return cached;

    const facilities = await storage.getAll('facilities');
    for (const facility of facilities) {
        const checklists = Array.isArray(facility.checklists) ? facility.checklists : [];
        for (const mapping of checklists) {
            if (mapping && mapping.mapping_id === mappingId && mapping.response_id) {
                return {
                    response_id: mapping.response_id,
                    status: mapping.response_status || 'draft'
                };
            }
        }
    }

    return null;
}

async function applyCachedChecklistMappingStatuses(storage: OfflineStorage, facilities: any[]) {
    if (!Array.isArray(facilities)) return facilities;

    for (const facility of facilities) {
        const checklists = Array.isArray(facility.checklists) ? facility.checklists : [];
        for (const mapping of checklists) {
            if (!mapping || !mapping.mapping_id) continue;
            const cached = await storage.getMeta(checklistMappingKey(mapping.mapping_id));
            if (!cached || !cached.response_id) continue;
            const cachedStatus = normalizeChecklistStatus(cached.status);
            const serverStatus = normalizeChecklistStatus(mapping.response_status);
            if (isLocalChecklistStatus(cachedStatus) || (cachedStatus === 'submitted' && serverStatus !== 'submitted')) {
                mapping.response_id = cached.response_id;
                mapping.response_status = cachedStatus;
            }
        }
    }

    return facilities;
}

async function writeChecklistResponse(
    params: any,
    storage: OfflineStorage,
    sync: SyncService,
    baseuri: string,
    namespace: string,
    funcName: string
) {
    const responseId = String((params && params.response_id) || '').trim();
    if (!responseId) {
        return { code: 400, data: null, message: '응답 ID가 없습니다.' };
    }

    const cached = await storage.getMeta(checklistResponseKey(responseId));
    if (!cached || !cached.response) {
        return {
            code: 503,
            data: null,
            message: '오프라인 저장을 위해서는 이 체크리스트를 온라인에서 한 번 열어야 합니다.'
        };
    }

    const answers = parseJson(params.answers, {});
    const meta = parseJson(params.meta, {});
    const isSubmit = funcName === 'submit_response';
    const cachedStatus = normalizeChecklistStatus(cached.response.status);
    const status = isSubmit
        ? 'submit_pending'
        : (cachedStatus === 'submitted' || cachedStatus === 'submit_pending' ? cachedStatus : 'draft');
    const photoCount = countChecklistPhotos(answers);
    const response = applyChecklistMeta(cached.response, meta, status);
    response.sync_source = 'local';
    response.sync_status = 'pending';
    response.sync_error = '';
    response.photo_count = photoCount;
    response.photo_sync_status = getChecklistPhotoSyncStatus(status, answers);
    if (isSubmit) response.submitted_local_at = checklistTimestamp();

    const next = {
        ...cached,
        response,
        answers
    };

    await storage.setMeta(checklistResponseKey(responseId), next);
    await storage.setMeta(checklistResultKey(responseId), next);
    await updateCachedChecklistMapping(storage, next.response.mapping_id, responseId, status);
    await sync.queueWrite({
        entity_type: 'checklist_response',
        entity_id: responseId,
        action: funcName,
        payload: params,
        api_endpoint: `${baseuri}/api/${namespace}/${funcName}`,
        operation_id: `checklist_response:${responseId}:${funcName}`,
        replace_actions: isSubmit ? ['save_response'] : []
    });

    return {
        code: 200,
        data: {
            saved: 'local',
            status,
            photo_sync_status: response.photo_sync_status,
            response: next.response
        },
        message: isSubmit
            ? '오프라인 제출은 서버 제출 완료가 아니라 제출 동기화 대기 상태로 저장되었습니다.'
            : '오프라인에 임시 저장되었습니다. 네트워크가 복구되면 동기화됩니다.'
    };
}

const offlineHandlers: Record<string, OfflineHandler> = {
    // === AUTH ===
    'component.auth.login/login': {
        write: async (params, storage) => {
            const userId = String((params && params.id) || '').trim();
            const passwordHash = String((params && params.password) || '');
            if (!userId || !passwordHash) {
                return { code: 401, data: '아이디 또는 비밀번호를 확인해주세요.' };
            }

            const cached = await storage.get('auth', credentialKey(userId));
            const digest = await credentialDigest(userId, passwordHash);
            if (!cached || cached.password_digest !== digest || !isCredentialUsable(cached)) {
                return { code: 401, data: OFFLINE_LOGIN_UNAVAILABLE_MESSAGE };
            }

            const autoLogin = toBool(params && params.auto_login)
                && toBool(cached.auto_login_enabled)
                && Number(cached.auto_login_expires_at || 0) > Date.now();
            const session = normalizeSessionUser(userId, cached.session);
            const offlineSession = createSessionCacheRecord(userId, {
                ...session,
                authenticated_at: Date.now()
            }, {
                autoLogin,
                autoLoginExpiresAt: cached.auto_login_expires_at,
                authMode: 'cache',
                offline: true,
                online: false
            });
            await storage.put('auth', offlineSession);
            return {
                code: 200,
                data: {
                    ...offlineSession,
                    id: offlineSession.user_id || offlineSession.login_id || userId
                }
            };
        },
        cache: async (result, params, storage) => {
            const userId = String((params && params.id) || '').trim();
            const passwordHash = String((params && params.password) || '');
            if (result && result.code === 200 && userId && passwordHash) {
                const autoLogin = toBool(params && params.auto_login);
                const session = normalizeSessionUser(userId, result.data);
                const current = await getCachedSessionRecord(storage);
                await storage.put('auth', createSessionCacheRecord(userId, session, {
                    autoLogin,
                    existing: current,
                    authMode: 'server',
                    offline: false
                }));
                await storage.put('auth', createCredentialCacheRecord(
                    userId,
                    await credentialDigest(userId, passwordHash),
                    session,
                    { autoLogin, serverPasswordHash: passwordHash }
                ));
            }
        }
    },
    'layout.navbar/session': {
        read: async (params, storage) => {
            const session = await getCachedSessionData(storage);
            if (session) return { code: 200, data: session };
            return { code: 401, data: null };
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data) {
                const userId = result.data.user_id || result.data.login_id || result.data.id || 'current_user';
                const session = normalizeSessionUser(userId, result.data);
                const current = await getCachedSessionRecord(storage);
                await storage.put('auth', createSessionCacheRecord(userId, session, {
                    existing: current,
                    authMode: 'server',
                    offline: false
                }));
            }
        }
    },
    'layout.field/session': {
        read: async (params, storage) => {
            const session = await getCachedSessionData(storage);
            if (session) return { code: 200, data: session };
            return { code: 401, data: null };
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data) {
                const userId = result.data.user_id || result.data.login_id || result.data.id || 'current_user';
                const session = normalizeSessionUser(userId, result.data);
                const current = await getCachedSessionRecord(storage);
                await storage.put('auth', createSessionCacheRecord(userId, session, {
                    existing: current,
                    authMode: 'server',
                    offline: false
                }));
            }
        }
    },

    // === HOME ===
    'page.home/init': {
        read: async (params, storage) => {
            const session = await getCachedSessionData(storage);
            if (!session) return { code: 401, data: null };
            const selectedProjects = await readCachedSelectedProjects(storage);
            return {
                code: 200,
                data: {
                    user: session,
                    initialized: true,
                    selected_projects: selectedProjects,
                    offline: true
                }
            };
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data && result.data.user) {
                const userId = result.data.user.user_id || result.data.user.login_id || result.data.user.id || 'current_user';
                const session = normalizeSessionUser(userId, result.data.user);
                const current = await getCachedSessionRecord(storage);
                await storage.put('auth', createSessionCacheRecord(userId, session, {
                    existing: current,
                    authMode: 'server',
                    offline: false
                }));
            }
            await cacheSelectedProjectsFromResult(result, storage);
        }
    },

    // === MAIN DASHBOARD ===
    'page.main/data': {
        read: async (params, storage) => {
            const projects = await storage.getAll('projects');
            const selected = await readCachedSelectedProjects(storage);

            const byType: any = {};
            for (const p of projects) {
                const t = p.type || '기타';
                if (!byType[t]) byType[t] = [];
                byType[t].push(p);
            }

            return {
                code: 200,
                data: {
                    projects,
                    selected_projects: selected,
                    selectedProjects: selected,
                    by_type: byType,
                    stats: {
                        total: projects.length,
                        진행중: projects.filter((p: any) => p.status === '진행중').length,
                        대기: projects.filter((p: any) => p.status === '대기').length,
                        완료: projects.filter((p: any) => p.status === '완료').length
                    }
                }
            };
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data) {
                const d = result.data;
                if (d.projects) await storage.putMany('projects', d.projects);
                if (d.grouped && typeof d.grouped === 'object') {
                    const groupedProjects = Object.keys(d.grouped).reduce((rows: any[], key: string) => {
                        return Array.isArray(d.grouped[key]) ? rows.concat(d.grouped[key]) : rows;
                    }, []);
                    if (groupedProjects.length > 0) await storage.putMany('projects', groupedProjects);
                }
                await cacheSelectedProjectsFromResult(result, storage);
            }
        }
    },
    'page.main/select': {
        write: async (params, storage, sync, baseuri, namespace, funcName) => {
            const userId = await getCurrentUserId(storage);
            const pid = String(params.project_id || '').trim();
            await addCachedProjectSelection(storage, userId, pid);
            await sync.queueWrite({
                entity_type: 'selection', entity_id: pid,
                action: 'select', payload: params,
                api_endpoint: `${baseuri}/api/${namespace}/${funcName}`
            });
            return { code: 200, data: true };
        }
    },
    'page.main/unselect': {
        write: async (params, storage, sync, baseuri, namespace, funcName) => {
            const userId = await getCurrentUserId(storage);
            const pid = String(params.project_id || '').trim();
            await removeCachedProjectSelection(storage, userId, pid);
            await sync.queueWrite({
                entity_type: 'selection', entity_id: pid,
                action: 'unselect', payload: params,
                api_endpoint: `${baseuri}/api/${namespace}/${funcName}`
            });
            return { code: 200, data: true };
        }
    },

    // === PROJECT LIST ===
    'page.project.list/list': {
        read: async (params, storage) => {
            let projects = await storage.getAll('projects');
            const text = params.text || params.search || '';
            const status = params.status || '';
            const type = params.type || '';

            if (text) {
                const q = text.toLowerCase();
                projects = projects.filter((p: any) =>
                    (p.title || '').toLowerCase().includes(q) ||
                    (p.category || '').toLowerCase().includes(q)
                );
            }
            if (status && status !== '전체') projects = projects.filter((p: any) => p.status === status);
            if (type && type !== '전체') projects = projects.filter((p: any) => p.type === type);

            // Add facility counts from cache
            for (const p of projects) {
                const facs = await storage.getAllByIndex('facilities', 'project_id', p.id);
                p.facility_count = facs.length;
            }

            const page = parseInt(params.page || '1');
            const dump = parseInt(params.dump || '20');
            const total = projects.length;
            const start = (page - 1) * dump;
            const rows = projects.slice(start, start + dump);

            return { code: 200, data: { list: rows, rows, total, page, dump } };
        },
        cache: async (result, params, storage) => {
            const rows = result && result.data ? (result.data.rows || result.data.list) : null;
            if (result && result.code === 200 && rows) {
                await storage.putMany('projects', rows);
            }
        }
    },
    'page.project.list/stats': {
        read: async (params, storage) => {
            const projects = await storage.getAll('projects');
            return {
                code: 200,
                data: {
                    total: projects.length,
                    by_status: {
                        대기: projects.filter((p: any) => p.status === '대기').length,
                        진행중: projects.filter((p: any) => p.status === '진행중').length,
                        완료: projects.filter((p: any) => p.status === '완료').length
                    },
                    by_type: projects.reduce((acc: any, p: any) => {
                        acc[p.type || '기타'] = (acc[p.type || '기타'] || 0) + 1;
                        return acc;
                    }, {})
                }
            };
        }
    },

    // === PROJECT DETAIL ===
    'page.project.detail/detail': {
        read: async (params, storage) => {
            return readCachedProjectDetail(storage, params.id);
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data) {
                await cacheProjectDetailData(storage, result.data);
            }
        }
    },

    // === INSPECTION ===
    'page.inspection/init': {
        read: async (params, storage) => {
            const projects = await readCachedSelectedProjects(storage);
            return { code: 200, data: { projects } };
        },
        cache: async (result, params, storage) => {
            await cacheSelectedProjectsFromResult(result, storage);
        }
    },

    // === CHECKLIST ===
    'page.checklist/init': {
        read: async (params, storage) => {
            const projects = await readCachedSelectedProjects(storage);
            return { code: 200, data: { projects } };
        },
        cache: async (result, params, storage) => {
            await cacheSelectedProjectsFromResult(result, storage);
        }
    },
    'page.checklist.facilities/load': {
        read: async (params, storage) => {
            const projectId = params.project_id;
            const project = await getCachedProjectById(storage, projectId);
            let facilities = await getCachedRowsByIndex(storage, 'facilities', 'project_id', projectId);
            if (facilities.length === 0) {
                const normalizedProjectId = normalizeCacheId(projectId);
                const allFacilities = await storage.getAll('facilities');
                facilities = allFacilities.filter((facility: any) => {
                    if (normalizeCacheId(facility?.project_id) === normalizedProjectId) return true;
                    const checklists = Array.isArray(facility?.checklists) ? facility.checklists : [];
                    return checklists.some((mapping: any) => normalizeCacheId(mapping?.project_id) === normalizedProjectId);
                });
            }
            await applyCachedChecklistMappingStatuses(storage, facilities);
            return { code: 200, data: { project, facilities } };
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data) {
                await cacheProjectDetailData(storage, {
                    project: result.data.project,
                    facilities: result.data.facilities
                });
                if (Array.isArray(result.data.facilities)) {
                    await applyCachedChecklistMappingStatuses(storage, result.data.facilities);
                    for (const facility of result.data.facilities) await mergeAndCacheFacility(storage, facility);
                }
            }
        }
    },
    'page.checklist.facilities/start_checklist': {
        write: async (params, storage) => {
            const mappingId = String((params && params.mapping_id) || '').trim();
            const cached = mappingId ? await findCachedChecklistMapping(storage, mappingId) : null;
            if (cached && cached.response_id) {
                return {
                    code: 200,
                    data: {
                        response_id: cached.response_id,
                        status: cached.status || 'draft',
                        offline: true
                    }
                };
            }

            return {
                code: 503,
                data: null,
                message: '오프라인에서 새 체크리스트를 시작하려면 온라인에서 먼저 체크리스트를 한 번 열어야 합니다.'
            };
        },
        cache: async (result, params, storage) => {
            const mappingId = String((params && params.mapping_id) || '').trim();
            const responseId = result && result.data ? result.data.response_id : '';
            if (result && result.code === 200 && mappingId && responseId) {
                await storage.setMeta(checklistMappingKey(mappingId), {
                    response_id: responseId,
                    status: result.data.status || 'draft',
                    cached_at: Date.now()
                });
            }
        }
    },
    'page.checklist.respond/load_response': {
        read: async (params, storage) => {
            const responseId = String((params && params.response_id) || '').trim();
            const cached = responseId ? await storage.getMeta(checklistResponseKey(responseId)) : null;
            if (!cached) {
                return {
                    code: 404,
                    data: null,
                    message: '캐시된 체크리스트 응답이 없습니다.'
                };
            }
            return { code: 200, data: cached };
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data) {
                await cacheChecklistAggregate(storage, result.data);
            }
        }
    },
    'page.checklist.respond/save_response': {
        write: writeChecklistResponse
    },
    'page.checklist.respond/submit_response': {
        write: writeChecklistResponse
    },
    'page.checklist.result/load_result': {
        read: async (params, storage) => {
            const responseId = String((params && params.response_id) || '').trim();
            const cachedResult = responseId ? await storage.getMeta(checklistResultKey(responseId)) : null;
            const cachedResponse = responseId ? await storage.getMeta(checklistResponseKey(responseId)) : null;
            const cached = cachedResult || cachedResponse;
            if (!cached) {
                return {
                    code: 404,
                    data: null,
                    message: '캐시된 체크리스트 결과가 없습니다.'
                };
            }

            if (!cached.project && cached.facility && cached.facility.project_id) {
                cached.project = await storage.get('projects', cached.facility.project_id);
            }
            return { code: 200, data: cached };
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data && result.data.response) {
                await cacheChecklistAggregate(storage, result.data);
            }
        }
    },

    // === INSPECTION VIEWER (핵심) ===
    'page.inspection.viewer/get_viewer_data': {
        read: async (params, storage) => {
            const facilityId = normalizeCacheId(params.facility_id);
            const drawingId = normalizeCacheId(params.drawing_id);

            const facility = await getCachedRecordById(storage, 'facilities', facilityId);
            const drawing = await getCachedRecordById(storage, 'drawings', drawingId);
            const missing = [];
            if (!facility) missing.push('시설물 데이터');
            if (!drawing) missing.push('도면 데이터');
            if (missing.length > 0) {
                return {
                    code: 404,
                    data: { missing },
                    message: `오프라인 캐시에 ${missing.join(', ')}가 없습니다.`
                };
            }
            let project = null;
            if (facility) project = await getCachedProjectById(storage, facility.project_id || drawing?.project_id);
            await cacheFacilityDrawingMapping(storage, facilityId, drawingId);

            const damages = await storage.get('damages', drawingId);
            const annotations = await storage.get('annotations', drawingId);
            const sync = await storage.getMeta(inspectionViewerSyncKey(drawingId));
            const overviewPhotos = facilityId ? await storage.getMeta(inspectionViewerOverviewKey(facilityId)) : [];
            const pdfFile = drawingId ? await storage.get('files', `pdf_${drawingId}`) : null;
            const offlineMissing = [];
            if (!hasCachedFileData(pdfFile)) {
                offlineMissing.push({
                    category: 'drawing_pdf',
                    label: drawing.filename || drawing.name || `도면 PDF ${drawingId}`,
                    ref_id: drawingId
                });
            }

            return {
                code: 200,
                data: {
                    facility: facility || {},
                    drawing: drawing || {},
                    project: project || {},
                    damages: damages ? damages.data : [],
                    drawings: annotations ? annotations.data : [],
                    overview_photos: Array.isArray(overviewPhotos) ? overviewPhotos : [],
                    sync: sync || { workspace_id: '', revision: 0, conflict_count: 0, conflicts: [] },
                    offline_missing: offlineMissing,
                    offline: true
                }
            };
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data) {
                const d = result.data;
                if (d.facility && d.facility.id) await mergeAndCacheFacility(storage, d.facility);
                if (d.drawing && d.drawing.id) await storage.put('drawings', d.drawing);
                if (d.project && d.project.id) await storage.put('projects', d.project);
                await cacheFacilityDrawingMapping(
                    storage,
                    params.facility_id || d.facility?.id,
                    params.drawing_id || d.drawing?.id
                );
                if (d.damages) {
                    await storage.put('damages', {
                        drawing_id: params.drawing_id,
                        data: d.damages,
                        synced_at: Date.now()
                    });
                }
                if (d.drawings) {
                    await storage.put('annotations', {
                        drawing_id: params.drawing_id,
                        data: d.drawings,
                        synced_at: Date.now()
                    });
                }
                if (d.overview_photos && params.facility_id) {
                    await storage.setMeta(inspectionViewerOverviewKey(params.facility_id), d.overview_photos);
                }
                if (d.sync && params.drawing_id) {
                    await storage.setMeta(inspectionViewerSyncKey(params.drawing_id), d.sync);
                }
            }
        }
    },
    'page.inspection.viewer/save': {
        write: async (params, storage, sync, baseuri, namespace, funcName) => {
            const drawingId = params.drawing_id;
            const operations = parseJson(params.operations, null);
            if (!Array.isArray(operations)) {
                return {
                    code: 400,
                    data: null,
                    message: '도면 오프라인 저장은 변경 로그(operations)가 필요합니다.'
                };
            }

            // Save damages locally
            if (params.damages) {
                let dmgData = params.damages;
                if (typeof dmgData === 'string') {
                    try { dmgData = JSON.parse(dmgData); } catch (e) { }
                }
                await storage.put('damages', {
                    drawing_id: drawingId,
                    data: dmgData,
                    localModified: Date.now()
                });
            }

            // Save drawings/annotations locally
            if (params.drawings) {
                let drwData = params.drawings;
                if (typeof drwData === 'string') {
                    try { drwData = JSON.parse(drwData); } catch (e) { }
                }
                await storage.put('annotations', {
                    drawing_id: drawingId,
                    data: drwData,
                    localModified: Date.now()
                });
            }
            if (params.overview_photos && params.facility_id) {
                await storage.setMeta(
                    inspectionViewerOverviewKey(params.facility_id),
                    parseJson(params.overview_photos, [])
                );
            }

            // Queue for sync
            await sync.queueWrite({
                entity_type: 'inspection_save',
                entity_id: drawingId,
                action: 'save',
                payload: params,
                api_endpoint: `${baseuri}/api/${namespace}/${funcName}`,
                operation_id: params.operation_id,
                client_id: params.client_id,
                base_revision: params.base_revision
            });

            return { code: 200, data: { saved: 'local', message: '로컬에 저장됨 (동기화 대기)' } };
        }
    },

    // === COMPONENT: PROJECTS ===
    'component.main.projects/data': {
        read: async (params, storage) => {
            const projects = await readCachedSelectedProjects(storage);
            return { code: 200, data: { selectedProjects: projects, selected_projects: projects } };
        },
        cache: async (result, params, storage) => {
            await cacheSelectedProjectsFromResult(result, storage);
        }
    },
    'component.main.projects/unselect': {
        write: async (params, storage, sync, baseuri, namespace, funcName) => {
            const userId = await getCurrentUserId(storage);
            const projectId = String(params.project_id || '').trim();
            await removeCachedProjectSelection(storage, userId, projectId);
            await sync.queueWrite({
                entity_type: 'selection', entity_id: projectId,
                action: 'unselect', payload: params,
                api_endpoint: `${baseuri}/api/${namespace}/${funcName}`
            });
            return { code: 200, data: true };
        }
    },

    // === FACILITY COMPONENTS ===
    'component.project.facilities/get_facility_detail': {
        read: async (params, storage) => {
            return readCachedFacilityDetail(storage, params.facility_id || params.id);
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data) {
                const data = result.data;
                await cacheProjectDetailData(storage, {
                    facilities: data.facility ? [data.facility] : [],
                    drawings: data.drawings || []
                });
                const facilityId = data.facility?.id || params.facility_id || params.id;
                if (Array.isArray(data.overview_photos) && facilityId) {
                    await storage.setMeta(inspectionViewerOverviewKey(facilityId), data.overview_photos);
                }
                if (facilityId && Array.isArray(data.drawings)) {
                    for (const drawing of data.drawings) await cacheFacilityDrawingMapping(storage, facilityId, drawing.id || drawing.drawing_id);
                }
            }
        }
    },
    'page.project.detail/get_facility_detail': {
        read: async (params, storage) => {
            return readCachedFacilityDetail(storage, params.facility_id || params.id);
        },
        cache: async (result, params, storage) => {
            if (result && result.code === 200 && result.data) {
                const data = result.data;
                await cacheProjectDetailData(storage, {
                    facilities: data.facility ? [data.facility] : [],
                    drawings: data.drawings || []
                });
                const facilityId = data.facility?.id || params.facility_id || params.id;
                if (Array.isArray(data.overview_photos) && facilityId) {
                    await storage.setMeta(inspectionViewerOverviewKey(facilityId), data.overview_photos);
                }
                if (facilityId && Array.isArray(data.drawings)) {
                    for (const drawing of data.drawings) await cacheFacilityDrawingMapping(storage, facilityId, drawing.id || drawing.drawing_id);
                }
            }
        }
    }
};

// Read-only API functions (used for determining if offline read is possible)
const READ_FUNCTIONS = new Set([
    'data', 'list', 'detail', 'session', 'init', 'get_viewer_data',
    'stats', 'users', 'get_facility_detail', 'download_drawing',
    'categories', 'search', 'download', 'load', 'load_response', 'load_result'
]);

export class OfflineInterceptor {
    private storage: OfflineStorage;
    private network: NetworkService;
    private sync: SyncService;

    constructor() {
        this.storage = getOfflineStorage();
        this.network = getNetworkService();
        this.sync = getSyncService();
    }

    async call(baseuri: string, namespace: string, funcName: string, data: any = {}, options: any = {}): Promise<any> {
        const handlerKey = `${namespace}/${funcName}`;
        const handler = offlineHandlers[handlerKey];
        const isRead = READ_FUNCTIONS.has(funcName);
        const url = `${baseuri}/api/${namespace}/${funcName}`;

        // If online, try server first
        if (this.network.isOnline) {
            try {
                const result = await this.serverCall(url, data, options);
                // Cache the response
                if (handler && handler.cache) {
                    try {
                        await handler.cache(result, data, this.storage);
                    } catch (e) {
                        console.warn('[OfflineInterceptor] Cache error:', e);
                    }
                }
                if (isRead && handler && handler.read && result && result.code !== undefined && Number(result.code) !== 200) {
                    const fallback = await this.tryReadFallback(handlerKey, handler, data, false);
                    if (fallback) return fallback;
                }
                return result;
            } catch (err: any) {
                const isNetworkError = !err || !err.httpStatus || err.httpStatus === 0 || err.statusText === 'timeout';
                const isAuthError = err && (err.httpStatus === 401 || err.httpStatus === 403);
                if (isNetworkError) {
                    this.network.setStatus('offline');
                }

                if (isRead && handler && handler.read && (!isAuthError || await this.hasCachedSession())) {
                    const fallback = await this.tryReadFallback(handlerKey, handler, data, isNetworkError);
                    if (fallback) return fallback;
                }
                if (isNetworkError && !isRead && handler && handler.write) {
                    console.warn(`[OfflineInterceptor] Server failed, using offline write fallback for ${handlerKey}`);
                    return handler.write(data, this.storage, this.sync, baseuri, namespace, funcName);
                }
                throw err;
            }
        }

        // Offline mode
        if (handler) {
            if (isRead && handler.read) {
                return handler.read(data, this.storage);
            }
            if (!isRead && handler.write) {
                return handler.write(data, this.storage, this.sync, baseuri, namespace, funcName);
            }
        }

        // No handler — return error
        return {
            code: 503,
            data: null,
            message: '오프라인 상태입니다. 이 기능은 인터넷 연결이 필요합니다.'
        };
    }

    private async tryReadFallback(
        handlerKey: string,
        handler: OfflineHandler,
        data: any,
        allowErrorResult: boolean
    ): Promise<any | null> {
        try {
            const fallback = await handler.read!(data, this.storage);
            if (fallback && (Number(fallback.code) === 200 || allowErrorResult)) {
                console.warn(`[OfflineInterceptor] Server failed, using cached fallback for ${handlerKey}`);
                return fallback;
            }
        } catch (fallbackError) {
            console.warn(`[OfflineInterceptor] Cached fallback failed for ${handlerKey}:`, fallbackError);
        }
        return null;
    }

    private async hasCachedSession(): Promise<boolean> {
        return !!(await getCachedSessionData(this.storage));
    }

    private serverCall(url: string, data: any, options: any): Promise<any> {
        return new Promise((resolve, reject) => {
            $.ajax({
                url,
                type: 'POST',
                data,
                crossDomain: /^https?:\/\//.test(url),
                xhrFields: {
                    withCredentials: true,
                    ...(options && options.xhrFields ? options.xhrFields : {})
                },
                timeout: DEFAULT_REQUEST_TIMEOUT_MS,
                ...options,
                success: (res: any) => resolve(res),
                error: (xhr: any, status: string, error: string) => {
                    const requestError: any = new Error(`${status}: ${error}`);
                    requestError.httpStatus = xhr ? xhr.status : 0;
                    requestError.statusText = status;
                    reject(requestError);
                }
            });
        });
    }
}

let _interceptorInstance: OfflineInterceptor | null = null;

export function getOfflineInterceptor(): OfflineInterceptor {
    if (!_interceptorInstance) {
        _interceptorInstance = new OfflineInterceptor();
    }
    return _interceptorInstance;
}
